import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useMutation } from "@tanstack/react-query";
import { updateItemInCollection } from "directus/src/operations";
import { invalidateAllQueries } from "utils/utils";
import { eventBus } from "utils/eventBus";
import { toast } from "sonner";
import { UserFieldInput } from "permissions/components/user-field-input";
import { ROUTE } from "@/lib/utils";

export default function DepartmentHeadList() {
  const { employeeId } = useParams();
  const permissions = usePermissions();
  const canEditHead = permissions.hasFieldAccess("departments", "update", "head");
  const userFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };

  const storageKey = employeeId ? `profile-dept-head-${employeeId}` : "profile-dept-head";

  const updateDeptMutation = useMutation({
    mutationFn: async ({ id, headId }) => {
      await updateItemInCollection("departments", id, { head: headId });
    },
    onSuccess: () => {
      invalidateAllQueries();
      toast.success("Department Head Updated Successfully");
      eventBus.emit("refresh:list", `departments${storageKey}`);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const fields = [
    "id",
    "name",
    "head.id",
    "head.first_name",
    "head.last_name",
    "head.avatar",
  ];

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
    },
    {
      id: "head",
      header: "Head",
      hidden: !canEditHead,
      href: false,
      cell: ({ row }) => {
        const dept = row.original;
        return (
          <UserFieldInput
            targetItem={dept.id}
            field="head"
            collection="departments"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="department_head"
              value={dept.head}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateDeptMutation.mutateAsync({
                        id: dept.id,
                        headId: selectedEmployee.id,
                      });
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    head: userFilter,
  };

  return (
    <List
      title="Departments"
      storageKey={storageKey}
      endPoint="departments"
      filters={filters}
      columns={columns.filter(col => !col.hidden)}
      meta={{ fields }}
      sortBy="name"
      href={(item) => ROUTE.DEPARTMENT(item.id)}
    />
  );
}
